import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { useCurrentLanguage } from '../hooks/useTranslation'
import { usePageSeo } from '../hooks/usePageSeo'
import { getOrganizationSchemaId, getSiteUrl, getWebsiteSchemaId, absoluteUrl } from '../lib/siteUrl'
import LocalAnswerBlock from '../components/LocalAnswerBlock.jsx'

const About = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const currentLanguage = useCurrentLanguage()
  const location = useLocation()

  const aboutTitle = `${t('about.hero.title')} | VIVA Psychotherapy`
  const aboutDescription = t('about.hero.description')
  const aboutKeywords = `about VIVA Psychotherapy, mental health therapist Boca Raton, licensed mental health counselor Florida, trauma therapy, bilingual therapy, Lidiana Cartolano, therapy services Boca Raton`

  usePageSeo({
    enabled: !isLoading,
    title: aboutTitle,
    description: aboutDescription,
    keywords: aboutKeywords,
    pathname: location.pathname
  })

  useEffect(() => {
    if (isLoading) return undefined
    const geoMeta = [
      { name: 'geo.region', content: 'US-FL' },
      { name: 'geo.placename', content: 'Boca Raton' },
      { name: 'geo.position', content: '26.3906;-80.0887' },
      { name: 'ICBM', content: '26.3906, -80.0887' }
    ]
    geoMeta.forEach(({ name, content }) => {
      let meta = document.querySelector(`meta[name="${name}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('name', name)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    })

    const pageUrl = absoluteUrl(location.pathname)
    const site = getSiteUrl()
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'MedicalBusiness',
          '@id': getOrganizationSchemaId(),
          name: 'VIVA Psychotherapy',
          description: aboutDescription,
          url: `${site}/`,
          telephone: '+1-561-759-7519',
          email: 'info@vivapsychotherapy.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5301 North Federal Highway Suite 200',
            addressLocality: 'Boca Raton',
            addressRegion: 'FL',
            postalCode: '33487',
            addressCountry: 'US'
          },
          founder: {
            '@type': 'Person',
            name: 'Lidiana Cartolano',
            jobTitle: 'Licensed Mental Health Counselor',
            description:
              'Licensed Mental Health Counselor with over 20 years of experience, specializing in trauma therapy, bilingual services (English and Portuguese)'
          },
          areaServed: [
            { '@type': 'State', name: 'Florida' },
            { '@type': 'City', name: 'Boca Raton' },
            { '@type': 'City', name: 'Delray Beach' },
            { '@type': 'City', name: 'Boynton Beach' },
            { '@type': 'City', name: 'West Palm Beach' },
            { '@type': 'City', name: 'Fort Lauderdale' },
            { '@type': 'AdministrativeArea', name: 'Palm Beach County' },
            { '@type': 'AdministrativeArea', name: 'Broward County' }
          ],
          serviceType: [
            'Mental Health Counseling',
            'Trauma Therapy',
            'Individual Therapy',
            'Group Therapy',
            'Family Counseling'
          ],
          availableLanguage: ['English', 'Portuguese'],
          paymentAccepted: ['Cash', 'Credit Card', 'Insurance', 'HSA', 'FSA'],
          sameAs: [
            'https://www.facebook.com/profile.php?id=61578353949539',
            'https://www.instagram.com/vivapsychotherapy',
            'https://www.psychologytoday.com/us/therapists/lidiana-cartolano-boca-raton-fl/1038382'
          ]
        },
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}#webpage`,
          url: pageUrl,
          name: aboutTitle,
          description: aboutDescription,
          isPartOf: { '@id': getWebsiteSchemaId() },
          about: { '@id': getOrganizationSchemaId() }
        }
      ]
    }

    let structuredDataScript = document.querySelector('script[type="application/ld+json"][data-about]')
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.setAttribute('type', 'application/ld+json')
      structuredDataScript.setAttribute('data-about', 'true')
      document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(structuredData)
    return undefined
  }, [isLoading, language, location.pathname, aboutTitle, aboutDescription])

  // Get translation arrays
  const educationItems = t('about.meetTherapist.education.items')
  const affiliationsItems = t('about.meetTherapist.affiliations.items')
  const licensesItems = t('about.meetTherapist.licenses.items')

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <main>
        {/* Hero Section with H1 for SEO */}
        <section className="bg-gradient-to-br from-primary-darkGreen to-primary-green py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {t('about.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-4">
                {t('about.hero.subtitle')}
              </p>
              <p className="text-lg text-white/80 max-w-2xl mx-auto">
                {t('about.hero.description')}
              </p>
            </div>
          </div>
        </section>

        <LocalAnswerBlock compact />

        {/* Section 1: Meet the Therapist */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-primary-darkGreen mb-12 text-center">
              {t('about.meetTherapist.title')}
            </h2>
            
            {/* Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Row 1 Column 1: Image */}
              <div className="flex justify-center lg:justify-start">
                <img
                  src="/img/lidiana/LidianaPortraitSitting.jpg"
                  alt="Lidiana Cartolano - Licensed Therapist"
                  className="w-full max-w-md rounded-lg shadow-lg object-cover"
                />
              </div>
              
              {/* Row 1 Column 2: Content */}
              <div className="flex items-center">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <div className="text-lg text-gray-700 leading-relaxed text-justify">
                    {t('about.meetTherapist.row1Content1').split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Professional Information and Image */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Image - Show first on mobile */}
              <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                <img
                  src="/img/lidiana/LidianaPortraitstanding-1.jpg"
                  alt="Lidiana Cartolano - Professional Portrait"
                  className="w-full max-w-md rounded-lg shadow-lg object-cover"
                />
              </div>
              
              {/* Left Column: All Professional Information - Show second on mobile */}
              <div className="space-y-6 order-2 lg:order-1">
                {/* Education Box */}
                <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary-darkGreen rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-primary-darkGreen">
                      {t('about.meetTherapist.education.title')}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {(educationItems || []).map((item, index) => (
                      <li key={index} className="flex items-start text-justify">
                        <div className="w-2 h-2 bg-primary-darkGreen rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Professional Affiliations Box */}
                <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary-darkGreen rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-primary-darkGreen">
                      {t('about.meetTherapist.affiliations.title')}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {(affiliationsItems || []).map((item, index) => (
                      <li key={index} className="flex items-start text-justify">
                        <div className="w-2 h-2 bg-primary-darkGreen rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Licenses Box */}
                <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-primary-darkGreen rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-primary-darkGreen">
                      {t('about.meetTherapist.licenses.title')}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {(licensesItems || []).map((item, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-primary-darkGreen rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 whitespace-pre-line">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Ready to take the first step */}
        <section className="bg-primary-lightGray py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-primary-darkGreen mb-8 text-center">
              {t('about.readyToStart.title')}
            </h2>
            
            {/* Row 1: Content */}
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                  {t('about.readyToStart.content')}
                </p>
              </div>
            </div>
            
            {/* Row 2: Image */}
            <div className="flex justify-center mb-8">
              <img
                src="/img/lidiana/LidianaPortraitDolls.jpg"
                alt="Lidiana Cartolano with therapeutic dolls"
                className="w-full max-w-2xl rounded-lg shadow-lg object-cover"
              />
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`tel:${t('common.contact.phone').replace(/\D/g, '')}`}
                className="px-8 py-3 bg-primary-darkGreen text-white rounded-lg hover:bg-primary-green transition-colors duration-200 font-semibold text-lg"
              >
                {t('common.buttons.contactUs')}
              </a>
              <a
                href={currentLanguage === 'pt' ? '/pt/contact' : '/contact'}
                className="px-8 py-3 bg-white text-primary-darkGreen border-2 border-primary-darkGreen rounded-lg hover:bg-primary-darkGreen hover:text-white transition-colors duration-200 font-semibold text-lg"
              >
                {t('common.buttons.scheduleConsultation', 'Schedule Consultation')}
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default About
